import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { Modal as ModalContainer, Box, styled } from '@mui/material';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import {
    Location,
    ManualPosJournalResponse
} from '@/services/PaymentsServiceV2';
import { DATE_FORMAT } from '@/lib/constants';
import { Dropdown } from '@/components/shared/Dropdown';
import { NumericInput } from '@/components/shared/NumericInput';
import { StyledDatePicker } from '@/components/shared/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Title, SMBold, SMRegular } from '@/typography/Typography';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { usePOSProvider } from '@/utils/providers';

const FieldGroup = styled(Box)`
    margin: 10px 0;
`;

interface AddManualJournalDialogProps {
    open: boolean;
    onClose: () => void;
    locations: Location[];
    journals: ManualPosJournalResponse[];
}

export const AddManualJournalDialog = ({
    open,
    onClose,
    locations,
    journals
}: AddManualJournalDialogProps) => {
    const { t } = useTranslation();
    const { addManualPosJournal } = usePaymentsMutations();
    const { getLogoForPOSProvider } = usePOSProvider();

    const [locationId, setLocationId] = useState<string>('');
    const [date, setDate] = useState<dayjs.Dayjs | undefined>(undefined);
    const [amount, setAmount] = useState<number | null>(null);

    const reset = () => {
        setLocationId('');
        setDate(undefined);
        setAmount(null);
    };

    const onCloseHandler = () => {
        addManualPosJournal.reset();
        reset();
        onClose();
    };

    useHandleResponse({
        error: addManualPosJournal.error,
        isSuccess: addManualPosJournal.isSuccess,
        successAlertMessage: t('tips.manual_journal_added'),
        isSuccessCallBack: onCloseHandler
    });

    // Preselect the only location so a single-location company skips a click.
    useEffect(() => {
        if (open && !locationId && locations.length === 1) {
            setLocationId(locations[0].locationId);
        }
    }, [open, locations, locationId]);

    const dateStr = date?.format(DATE_FORMAT.SERVER_DATE);
    const isDuplicate =
        !!locationId &&
        !!dateStr &&
        journals.some((j) => j.locationId === locationId && j.date === dateStr);

    const submitDisabled =
        !locationId ||
        !dateStr ||
        amount === null ||
        isDuplicate ||
        addManualPosJournal.isPending;

    return (
        <ModalContainer open={open} onClose={onCloseHandler}>
            <ConfirmationBox style={{ width: '460px' }}>
                <ConnectHeader
                    logo={getLogoForPOSProvider()}
                    title={t('tips.add_journal')}
                    description={t('tips.add_journal_description')}
                />

                <FieldGroup>
                    <Title mb={0.5}>{t('tips.location_name')}</Title>
                    <Dropdown
                        label={t('tips.select_location')}
                        value={locationId}
                        choices={locations.map((l) => ({
                            label: l.locationName,
                            value: l.locationId
                        }))}
                        onChange={(val) => setLocationId(val as string)}
                    />
                </FieldGroup>

                <FieldGroup>
                    <Title mb={0.5}>{t('date')}</Title>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <StyledDatePicker
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    variant: 'outlined',
                                    placeholder: 'DD/MM/YYYY'
                                }
                            }}
                            format={DATE_FORMAT.BASIC_DATA_YEAR}
                            value={date || null}
                            onChange={(newValue: any | null) => {
                                if (newValue && dayjs(newValue).isValid()) {
                                    setDate(dayjs(newValue));
                                } else {
                                    setDate(undefined);
                                }
                            }}
                            views={['year', 'month', 'day']}
                            openTo="day"
                        />
                    </LocalizationProvider>
                </FieldGroup>

                <FieldGroup>
                    <Title mb={0.5}>{t('tips.total_tips_amount')}</Title>
                    <NumericInput
                        value={amount}
                        onChange={(val) => setAmount(val)}
                        placeholder={t('tips.enter_new_amount')}
                        helperText={t('tips.manual_journal_amount_helper')}
                    />
                </FieldGroup>

                {isDuplicate && (
                    <SMRegular color="error" mt={0.5}>
                        {t('tips.manual_journal_duplicate')}
                    </SMRegular>
                )}

                <Box
                    m={'20px 0'}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <OutlinedSecondaryButtonLG
                        sx={{ width: '48%' }}
                        onClick={onCloseHandler}
                    >
                        <SMBold textTransform={'none'}>{t('cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        sx={{ width: '48%' }}
                        loading={addManualPosJournal.isPending}
                        disabled={submitDisabled}
                        onClick={() => {
                            addManualPosJournal.mutate({
                                locationId,
                                date: dateStr as string,
                                amount: amount as number
                            });
                        }}
                    >
                        <SMBold textTransform={'none'}>
                            {t('tips.add_journal')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
